import { useState, useEffect, useCallback, useMemo } from "react"
import {
  Container,
  Typography,
  Box,
  Stack,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Sheet,
  Table,
  Select,
  Option,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Alert,
  Divider,
  LinearProgress
} from "@mui/joy"
import {
  Refresh as RefreshIcon,
  Email as EmailIcon,
  TrendingUp as TrendIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ErrorOutline as ErrorIcon,
  Visibility as ViewIcon,
  Mouse as ClickIcon,
  Send as SendIcon,
  CloudSync as SyncIcon,
  Campaign as CampaignIcon,
  Schedule as ScheduleIcon,
  Block as BounceIcon
} from "@mui/icons-material"
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  PieChart,
  Pie,
  Cell
} from "recharts"
import { toast } from "sonner"
import api from "../services/api"

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

type Period = "today" | "week" | "month" | "year" | "all"
type Granularity = "hour" | "day" | "week" | "month"

interface KpiSummary {
  campaigns: {
    total: number
    drafts: number
    scheduled: number
    running: number
    finished: number
    cancelled: number
  }
  recipients: {
    total: number
    pending: number
    queued: number
    sent: number
    delivered: number
    opened: number
    clicked: number
    bounced: number
    failed: number
    unsubscribed: number
    spamComplaints: number
  }
  rates: {
    deliveryRate: number
    openRate: number
    clickRate: number
    bounceRate: number
    failureRate: number
  }
  domains: Record<string, { sent: number; failed: number; opened: number }>
}

interface TrendPoint {
  bucket: string
  sent: number
  delivered: number
  opened: number
  clicked: number
  bounced: number
  failed: number
}

interface CampaignRow {
  id: number
  name: string
  subject: string
  status: string
  provider: string | null
  dispatchMode: string
  sendAt: string | null
  completedAt: string | null
  createdAt: string
  totalRecipients: number
  totalSent: number
  totalDelivered: number
  totalOpened: number
  totalClicked: number
  totalBounced: number
  totalFailed: number
  contactListName: string | null
}

interface FailureRow {
  email: string
  campaignId: number
  campaignName: string
  status: string
  errorMessage: string | null
  bounceReason: string | null
  bouncedAt: string | null
  sentAt: string | null
}

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

const COLORS = {
  primary: "#3b82f6",
  success: "#52b788",
  warning: "#f3a43b",
  danger: "#ef4444",
  neutral: "#94a3b8",
  bg: "#f8f9fa",
  dark: "#1e293b"
}

const PERIOD_LABELS: Record<Period, string> = {
  today: "Hoy",
  week: "Última semana",
  month: "Último mes",
  year: "Último año",
  all: "Todo"
}

const STATUS_LABELS: Record<string, string> = {
  INACTIVA: "Borrador",
  BORRADOR: "Borrador",
  PROGRAMADA: "Programada",
  EN_ANDAMENTO: "En proceso",
  EN_PROCESO: "En proceso",
  FINALIZADA: "Finalizada",
  ENVIADA: "Enviada",
  CANCELADA: "Cancelada"
}

const STATUS_COLORS: Record<string, "success" | "primary" | "warning" | "neutral" | "danger"> = {
  FINALIZADA: "success",
  ENVIADA: "success",
  EN_ANDAMENTO: "primary",
  EN_PROCESO: "primary",
  PROGRAMADA: "warning",
  INACTIVA: "neutral",
  BORRADOR: "neutral",
  CANCELADA: "danger"
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string | null): string {
  if (!iso) return "—"
  const d = new Date(iso)
  return d.toLocaleString("es-ES", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  })
}

function formatBucket(bucket: string, granularity: Granularity): string {
  const d = new Date(bucket)
  if (granularity === "hour") {
    return d.toLocaleString("es-ES", { hour: "2-digit", minute: "2-digit" })
  }
  if (granularity === "day") {
    return d.toLocaleDateString("es-ES", { month: "short", day: "2-digit" })
  }
  if (granularity === "week") {
    return `Sem ${Math.ceil(d.getDate() / 7)} ${d.toLocaleDateString("es-ES", { month: "short" })}`
  }
  return d.toLocaleDateString("es-ES", { year: "numeric", month: "short" })
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export default function EmailMarketingDashboard() {
  const [period, setPeriod] = useState<Period>("month")
  const [granularity, setGranularity] = useState<Granularity>("day")

  const [kpis, setKpis] = useState<KpiSummary | null>(null)
  const [trend, setTrend] = useState<TrendPoint[]>([])
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([])
  const [campaignsCount, setCampaignsCount] = useState(0)
  const [failures, setFailures] = useState<FailureRow[]>([])

  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [campaignTab, setCampaignTab] = useState<"all" | "FINALIZADA" | "PROGRAMADA" | "EN_ANDAMENTO" | "CANCELADA">("all")

  // -------------------------------------------------------------------------
  // Carga
  // -------------------------------------------------------------------------

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const status = campaignTab === "all" ? undefined : campaignTab
      const [r1, r2, r3, r4] = await Promise.all([
        api.get(`/email-marketing/dashboard/kpis?period=${period}`),
        api.get(`/email-marketing/dashboard/trend?period=${period}&granularity=${granularity}`),
        api.get(`/email-marketing/dashboard/campaigns?period=${period}&pageSize=20${status ? `&status=${status}` : ""}`),
        api.get(`/email-marketing/dashboard/failures?period=${period}&limit=20`)
      ])
      setKpis(r1.data?.data || null)
      setTrend(r2.data?.data || [])
      setCampaigns(r3.data?.records || [])
      setCampaignsCount(r3.data?.count || 0)
      setFailures(r4.data?.data || [])
    } catch (err) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      toast.error(ax.response?.data?.message || ax.message || "Error cargando dashboard")
    } finally {
      setLoading(false)
    }
  }, [period, granularity, campaignTab])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const handleSync = async () => {
    setSyncing(true)
    try {
      const { data } = await api.post(`/email-marketing/dashboard/sync`)
      toast.success(`Sync completo: ${data.updated} campañas actualizadas, ${data.recipientsCreated || 0} recipients creados`)
      await fetchAll()
    } catch (err) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      toast.error(ax.response?.data?.message || ax.message || "Error en sync")
    } finally {
      setSyncing(false)
    }
  }

  // -------------------------------------------------------------------------
  // Datos derivados
  // -------------------------------------------------------------------------

  const pieDomainData = useMemo(() => {
    if (!kpis) return []
    return Object.entries(kpis.domains)
      .map(([name, v]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value: v.sent
      }))
      .filter(d => d.value > 0)
  }, [kpis])

  const trendChartData = useMemo(() => {
    return trend.map(t => ({
      ...t,
      label: formatBucket(t.bucket, granularity)
    }))
  }, [trend, granularity])

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{ mb: 3, gap: 1.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44, height: 44, borderRadius: "md",
              bgcolor: "primary.softBg",
              display: "flex", alignItems: "center", justifyContent: "center"
            }}
          >
            <EmailIcon sx={{ color: "primary.plainColor", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography level="h3" sx={{ fontWeight: 700 }}>
              Dashboard Email Marketing
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
              Métricas en vivo de campañas, entregas y aperturas
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Select
            size="sm"
            value={period}
            onChange={(_e, v) => setPeriod((v || "month") as Period)}
            sx={{ minWidth: 160 }}
          >
            {Object.entries(PERIOD_LABELS).map(([k, l]) => (
              <Option key={k} value={k}>{l}</Option>
            ))}
          </Select>
          <Select
            size="sm"
            value={granularity}
            onChange={(_e, v) => setGranularity((v || "day") as Granularity)}
            sx={{ minWidth: 120 }}
          >
            <Option value="hour">Por hora</Option>
            <Option value="day">Por día</Option>
            <Option value="week">Por semana</Option>
            <Option value="month">Por mes</Option>
          </Select>
          <Tooltip title="Sincronizar con provider">
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              startDecorator={syncing ? <CircularProgress size="sm" /> : <SyncIcon fontSize="small" />}
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? "Sincronizando..." : "Sincronizar"}
            </Button>
          </Tooltip>
          <Tooltip title="Recargar">
            <IconButton variant="outlined" color="neutral" size="sm" onClick={fetchAll} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {loading && !kpis ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : !kpis ? (
        <Alert color="warning" variant="soft">No hay datos disponibles</Alert>
      ) : (
        <>
          {/* KPI Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <KpiCard
              title="Campañas"
              value={kpis.campaigns.total}
              subtitle={`${kpis.campaigns.finished} finalizadas, ${kpis.campaigns.scheduled} programadas`}
              icon={<CampaignIcon />}
              color={COLORS.primary}
            />
            <KpiCard
              title="Emails enviados"
              value={kpis.recipients.delivered + kpis.recipients.opened + kpis.recipients.clicked + kpis.recipients.bounced}
              subtitle={`Tasa entrega ${kpis.rates.deliveryRate}%`}
              icon={<SendIcon />}
              color={COLORS.success}
            />
            <KpiCard
              title="Aperturas"
              value={kpis.recipients.opened + kpis.recipients.clicked}
              subtitle={`Tasa apertura ${kpis.rates.openRate}%`}
              icon={<ViewIcon />}
              color={COLORS.warning}
            />
            <KpiCard
              title="Fallidos"
              value={kpis.recipients.failed + kpis.recipients.bounced}
              subtitle={`Tasa rebote ${kpis.rates.bounceRate}%`}
              icon={<ErrorIcon />}
              color={COLORS.danger}
            />
          </Grid>

          {/* Tendencia + Domain breakdown */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid xs={12} md={8}>
              <Card variant="outlined" sx={{ borderRadius: "lg", height: "100%" }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Typography level="title-md" sx={{ fontWeight: 600 }}>
                      Tendencia de envíos
                    </Typography>
                    <Chip size="sm" variant="soft" startDecorator={<TrendIcon sx={{ fontSize: 16 }} />}>
                      {PERIOD_LABELS[period]} · {granularity}
                    </Chip>
                  </Stack>
                  {trendChartData.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: "center", color: "text.tertiary" }}>
                      <Typography level="body-sm">Sin datos en este periodo</Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={trendChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gradSent" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.4} />
                            <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gradOpened" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.4} />
                            <stop offset="95%" stopColor={COLORS.success} stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gradFailed" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={COLORS.danger} stopOpacity={0.4} />
                            <stop offset="95%" stopColor={COLORS.danger} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="label" stroke="#94a3b8" fontSize={11} />
                        <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                        <ChartTooltip />
                        <Legend />
                        <Area type="monotone" dataKey="sent" name="Enviados" stroke={COLORS.primary} fillOpacity={1} fill="url(#gradSent)" />
                        <Area type="monotone" dataKey="opened" name="Abiertos" stroke={COLORS.success} fillOpacity={1} fill="url(#gradOpened)" />
                        <Area type="monotone" dataKey="failed" name="Fallidos" stroke={COLORS.danger} fillOpacity={1} fill="url(#gradFailed)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid xs={12} md={4}>
              <Card variant="outlined" sx={{ borderRadius: "lg", height: "100%" }}>
                <CardContent>
                  <Typography level="title-md" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Por dominio
                  </Typography>
                  {pieDomainData.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: "center", color: "text.tertiary" }}>
                      <Typography level="body-sm">Sin envíos</Typography>
                    </Box>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie data={pieDomainData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                            {pieDomainData.map((_, i) => (
                              <Cell key={i} fill={[COLORS.primary, COLORS.success, COLORS.warning, COLORS.danger, COLORS.neutral][i % 5]} />
                            ))}
                          </Pie>
                          <ChartTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <Stack spacing={0.75} sx={{ mt: 1 }}>
                        {Object.entries(kpis.domains).filter(([, v]) => v.sent > 0).map(([name, v]) => (
                          <Stack key={name} direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Typography level="body-xs" sx={{ textTransform: "capitalize", fontWeight: 500 }}>
                                {name}
                              </Typography>
                              {v.failed > 0 && (
                                <Chip size="sm" variant="soft" color="danger">{v.failed} fail</Chip>
                              )}
                            </Stack>
                            <Typography level="body-xs" sx={{ fontWeight: 600 }}>
                              {v.sent} envíos
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Conversion Funnel */}
          <Card variant="outlined" sx={{ borderRadius: "lg", mb: 3 }}>
            <CardContent>
              <Typography level="title-md" sx={{ fontWeight: 600, mb: 2 }}>
                Embudo de conversión
              </Typography>
              <Stack spacing={1.5}>
                <FunnelRow
                  label="Enviados"
                  value={kpis.recipients.delivered + kpis.recipients.opened + kpis.recipients.clicked + kpis.recipients.bounced}
                  total={kpis.recipients.total}
                  color={COLORS.primary}
                />
                <FunnelRow
                  label="Entregados"
                  value={kpis.recipients.delivered + kpis.recipients.opened + kpis.recipients.clicked}
                  total={kpis.recipients.total}
                  color={COLORS.success}
                />
                <FunnelRow
                  label="Abiertos"
                  value={kpis.recipients.opened + kpis.recipients.clicked}
                  total={kpis.recipients.total}
                  color={COLORS.warning}
                />
                <FunnelRow
                  label="Click"
                  value={kpis.recipients.clicked}
                  total={kpis.recipients.total}
                  color="#a855f7"
                />
                <FunnelRow
                  label="Rebotados"
                  value={kpis.recipients.bounced}
                  total={kpis.recipients.total}
                  color={COLORS.danger}
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Tabs: Campañas + Fallos */}
          <Tabs value={campaignTab} onChange={(_e, v) => setCampaignTab((v || "all") as typeof campaignTab)}>
            <TabList sx={{ mb: 1.5 }}>
              <Tab value="all">Todas las campañas ({campaignsCount})</Tab>
              <Tab value="FINALIZADA">Finalizadas</Tab>
              <Tab value="PROGRAMADA">Programadas</Tab>
              <Tab value="EN_ANDAMENTO">En proceso</Tab>
              <Tab value="CANCELADA">Canceladas</Tab>
            </TabList>

            <TabPanel value={campaignTab} sx={{ p: 0 }}>
              <Sheet variant="outlined" sx={{ borderRadius: "lg", overflow: "auto" }}>
                <Table size="sm" hoverRow stickyHeader>
                  <thead>
                    <tr>
                      <th style={{ minWidth: 220 }}>Campaña</th>
                      <th style={{ width: 110 }}>Estado</th>
                      <th style={{ width: 100 }}>Provider</th>
                      <th style={{ width: 90, textAlign: "right" }}>Enviados</th>
                      <th style={{ width: 90, textAlign: "right" }}>Entregados</th>
                      <th style={{ width: 90, textAlign: "right" }}>Aperturas</th>
                      <th style={{ width: 90, textAlign: "right" }}>Clicks</th>
                      <th style={{ width: 90, textAlign: "right" }}>Fallidos</th>
                      <th style={{ width: 130 }}>Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.length === 0 ? (
                      <tr>
                        <td colSpan={9} style={{ textAlign: "center", padding: 24, color: "#94a3b8" }}>
                          Sin campañas en este periodo
                        </td>
                      </tr>
                    ) : (
                      campaigns.map(c => (
                        <tr key={c.id}>
                          <td>
                            <Stack spacing={0.25}>
                              <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                {c.name}
                              </Typography>
                              <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                                {c.subject?.slice(0, 50)}
                              </Typography>
                              {c.contactListName && (
                                <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                                  📋 {c.contactListName}
                                </Typography>
                              )}
                            </Stack>
                          </td>
                          <td>
                            <Chip size="sm" variant="soft" color={STATUS_COLORS[c.status] || "neutral"}>
                              {STATUS_LABELS[c.status] || c.status}
                            </Chip>
                          </td>
                          <td>
                            {c.provider ? (
                              <Chip size="sm" variant="outlined" color="primary">{c.provider}</Chip>
                            ) : (
                              <Chip size="sm" variant="soft" color="neutral">—</Chip>
                            )}
                          </td>
                          <td style={{ textAlign: "right" }}>{c.totalSent}</td>
                          <td style={{ textAlign: "right" }}>{c.totalDelivered}</td>
                          <td style={{ textAlign: "right", color: COLORS.success }}>{c.totalOpened}</td>
                          <td style={{ textAlign: "right", color: COLORS.primary }}>{c.totalClicked}</td>
                          <td style={{ textAlign: "right", color: c.totalFailed + c.totalBounced > 0 ? COLORS.danger : "inherit" }}>
                            {c.totalFailed + c.totalBounced}
                          </td>
                          <td>
                            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                              {formatDate(c.completedAt || c.sendAt || c.createdAt)}
                            </Typography>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </Sheet>
            </TabPanel>
          </Tabs>

          {/* Tabla fallos detallados */}
          {failures.length > 0 && (
            <Card variant="outlined" sx={{ borderRadius: "lg", mt: 3 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <BounceIcon color="error" />
                  <Typography level="title-md" sx={{ fontWeight: 600 }}>
                    Recipients fallidos ({failures.length})
                  </Typography>
                </Stack>
                <Sheet variant="outlined" sx={{ borderRadius: "md", overflow: "auto" }}>
                  <Table size="sm" hoverRow>
                    <thead>
                      <tr>
                        <th style={{ minWidth: 220 }}>Email</th>
                        <th style={{ minWidth: 200 }}>Campaña</th>
                        <th style={{ width: 100 }}>Estado</th>
                        <th style={{ minWidth: 250 }}>Razón</th>
                        <th style={{ width: 130 }}>Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {failures.map((f, i) => (
                        <tr key={i}>
                          <td>
                            <Typography level="body-sm" sx={{ fontWeight: 500 }}>{f.email}</Typography>
                          </td>
                          <td>
                            <Typography level="body-xs">{f.campaignName}</Typography>
                          </td>
                          <td>
                            <Chip size="sm" variant="soft" color="danger">
                              {f.status}
                            </Chip>
                          </td>
                          <td>
                            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                              {f.bounceReason || f.errorMessage || "—"}
                            </Typography>
                          </td>
                          <td>
                            <Typography level="body-xs">
                              {formatDate(f.bouncedAt || f.sentAt)}
                            </Typography>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Sheet>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Container>
  )
}

// ===========================================================================
// Subcomponentes
// ===========================================================================

function KpiCard({
  title,
  value,
  subtitle,
  icon,
  color
}: {
  title: string
  value: number
  subtitle: string
  icon: React.ReactNode
  color: string
}) {
  return (
    <Grid xs={12} sm={6} md={3}>
      <Card variant="outlined" sx={{ borderRadius: "lg", height: "100%", borderLeft: `3px solid ${color}` }}>
        <CardContent>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Box>
              <Typography level="body-xs" sx={{ color: "text.tertiary", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
                {title}
              </Typography>
              <Typography level="h2" sx={{ fontWeight: 800, color: "text.primary", mt: 0.5 }}>
                {value.toLocaleString("es-ES")}
              </Typography>
              <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.5 }}>
                {subtitle}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 36, height: 36, borderRadius: "md",
                bgcolor: `${color}15`,
                color,
                display: "flex", alignItems: "center", justifyContent: "center"
              }}
            >
              {icon}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  )
}

function FunnelRow({
  label,
  value,
  total,
  color
}: {
  label: string
  value: number
  total: number
  color: string
}) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography level="body-sm" sx={{ fontWeight: 500 }}>{label}</Typography>
        <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
          <strong>{value.toLocaleString("es-ES")}</strong> · {pct}%
        </Typography>
      </Stack>
      <LinearProgress
        determinate
        value={pct}
        sx={{
          "--LinearProgress-progressThickness": "8px",
          "--LinearProgress-thickness": "8px",
          "--LinearProgress-progressColor": color
        }}
      />
    </Box>
  )
}
